import { useAuth0 } from '@auth0/auth0-react'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Loader,
  Banner,
  Breadcrumbs,
  Button,
  InlineCopyText,
  Input,
  Label,
  LayerCard,
  Select,
  useKumoToastManager,
} from '@cloudflare/kumo'
import { HouseIcon, BuildingsIcon } from '@phosphor-icons/react'
import { apiRequest, ApiError } from '../api/client'
import {
  generatePublicCalendarToken,
  getPublicCalendarSubscribeInfo,
  revokePublicCalendarToken,
} from '../api/rooms'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { useTranslation } from '../i18n'
import { PageHeader } from '../components/kumo/page-header/page-header'
import type { AdminRoomView, Building, RoomType, RoomStatus } from '@hub-wifi/shared'

/* ------------------------------------------------------------------ */
/*  Form Data                                                          */
/* ------------------------------------------------------------------ */

interface RoomFormData {
  name: string
  capacity: number
  type: RoomType
  photoUrl: string
  floor: string
  status: RoomStatus
  mailbox: string
}

const emptyForm: RoomFormData = {
  name: '',
  capacity: 1,
  type: 'meeting',
  photoUrl: '',
  floor: '',
  status: 'available',
  mailbox: '',
}

/* ------------------------------------------------------------------ */
/*  RoomFormPage                                                        */
/* ------------------------------------------------------------------ */

export function RoomFormPage() {
  const { buildingId, roomId } = useParams<{ buildingId: string; roomId: string }>()
  const isEdit = Boolean(roomId)
  const navigate = useNavigate()
  const { getAccessTokenSilently } = useAuth0()
  const { t } = useTranslation()
  const toasts = useKumoToastManager()

  const [form, setForm] = useState<RoomFormData>(emptyForm)
  const [buildingName, setBuildingName] = useState<string>('')
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasPublicCalendarToken, setHasPublicCalendarToken] = useState(false)

  useEffect(() => {
    if (!buildingId) return

    async function fetchData() {
      try {
        const token = await getAccessTokenSilently()

        // Fetch building name for breadcrumbs
        const building = await apiRequest<Building>(`/api/buildings/${buildingId}`, { token })
        setBuildingName(building.name)

        // Fetch room data for edit mode
        if (roomId) {
          const rooms = await apiRequest<AdminRoomView[]>(`/api/buildings/${buildingId}/rooms`, {
            token,
          })
          const room = rooms.find((r) => r.id === roomId)
          if (room) {
            setForm({
              name: room.name,
              capacity: room.capacity,
              type: room.type,
              photoUrl: room.photoUrl ?? '',
              floor: room.floor ?? '',
              status: room.status,
              mailbox: room.mailbox ?? '',
            })
            setHasPublicCalendarToken(room.hasPublicCalendarToken)
          } else {
            setError(t('rooms.notFound'))
          }
        }
      } catch (err) {
        setError(err instanceof ApiError ? err.message : t('common.error'))
      } finally {
        setLoading(false)
      }
    }

    void fetchData()
  }, [buildingId, roomId, getAccessTokenSilently, t])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSubmitting(true)

    try {
      const token = await getAccessTokenSilently()
      const payload = {
        ...form,
        photoUrl: form.photoUrl || null,
        floor: form.floor || null,
        mailbox: form.mailbox.trim() ? form.mailbox.trim().toLowerCase() : null,
      }

      if (isEdit) {
        await apiRequest(`/api/buildings/${buildingId}/rooms/${roomId}`, {
          method: 'PUT',
          token,
          body: payload,
        })
        toasts.add({ variant: 'success', title: t('rooms.updateSuccess') })
      } else {
        await apiRequest(`/api/buildings/${buildingId}/rooms`, {
          method: 'POST',
          token,
          body: payload,
        })
        toasts.add({ variant: 'success', title: t('rooms.createSuccess') })
      }
      navigate(`/buildings/${buildingId}/rooms`)
    } catch (err) {
      toasts.add({
        variant: 'error',
        title: t('common.errorTitle'),
        description: err instanceof ApiError ? err.message : t('common.error'),
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div>
      <PageHeader
        breadcrumbs={
          <Breadcrumbs>
            <Breadcrumbs.Link icon={<HouseIcon size={14} />} href="/">
              {t('nav.home')}
            </Breadcrumbs.Link>
            <Breadcrumbs.Separator />
            <Breadcrumbs.Link icon={<BuildingsIcon size={14} />} href="/buildings">
              {t('buildings.title')}
            </Breadcrumbs.Link>
            <Breadcrumbs.Separator />
            {buildingName && (
              <>
                <Breadcrumbs.Link href="/buildings">{buildingName}</Breadcrumbs.Link>
                <Breadcrumbs.Separator />
              </>
            )}
            <Breadcrumbs.Current>
              {isEdit ? t('rooms.editRoom') : t('rooms.createRoom')}
            </Breadcrumbs.Current>
          </Breadcrumbs>
        }
        title={isEdit ? t('rooms.editRoom') : t('rooms.createRoom')}
        description={t('rooms.pageDescription')}
      />

      {loading && (
        <div className="flex items-center justify-center py-16">
          <Loader size="lg" />
        </div>
      )}

      {!loading && error && (
        <Banner variant="error">
          {t('common.errorPrefix')} {error}
        </Banner>
      )}

      {!loading && !error && (
        <LayerCard>
          <LayerCard.Primary>
            <form onSubmit={(e) => void handleSubmit(e)} className="flex flex-col gap-4">
              <div className="flex flex-col gap-1">
                <Label>{t('rooms.name')}</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-1">
                  <Label>{t('rooms.capacity')}</Label>
                  <Input
                    type="number"
                    min={1}
                    value={String(form.capacity)}
                    onChange={(e) => setForm({ ...form, capacity: Number(e.target.value) })}
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <Label>{t('rooms.type')}</Label>
                  <Select
                    value={form.type}
                    onValueChange={(v) => {
                      if (v) setForm({ ...form, type: v as RoomType })
                    }}
                  >
                    <Select.Option value="meeting">{t('rooms.type.meeting')}</Select.Option>
                    <Select.Option value="phone-booth">{t('rooms.type.phone-booth')}</Select.Option>
                    <Select.Option value="event">{t('rooms.type.event')}</Select.Option>
                    <Select.Option value="private-office">
                      {t('rooms.type.private-office')}
                    </Select.Option>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-1">
                  <Label>{t('rooms.floor')}</Label>
                  <Input
                    value={form.floor}
                    onChange={(e) => setForm({ ...form, floor: e.target.value })}
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <Label>{t('rooms.status')}</Label>
                  <Select
                    value={form.status}
                    onValueChange={(v) => {
                      if (v) setForm({ ...form, status: v as RoomStatus })
                    }}
                  >
                    <Select.Option value="available">{t('rooms.status.available')}</Select.Option>
                    <Select.Option value="occupied">{t('rooms.status.occupied')}</Select.Option>
                    <Select.Option value="maintenance">
                      {t('rooms.status.maintenance')}
                    </Select.Option>
                  </Select>
                </div>
              </div>

              <div className="flex flex-col gap-1">
                <Label>{t('rooms.photoUrl')}</Label>
                <Input
                  value={form.photoUrl}
                  onChange={(e) => setForm({ ...form, photoUrl: e.target.value })}
                  placeholder="https://..."
                />
              </div>

              <div className="flex flex-col gap-1">
                <Label>{t('rooms.mailbox')}</Label>
                <Input
                  type="email"
                  value={form.mailbox}
                  onChange={(e) => setForm({ ...form, mailbox: e.target.value })}
                  placeholder="nintendospace@tesset.com"
                />
                <span className="text-xs text-kumo-subtle">{t('rooms.mailboxHelp')}</span>
              </div>

              {isEdit && roomId && buildingId && (
                <PublicCalendarSection
                  buildingId={buildingId}
                  roomId={roomId}
                  hasToken={hasPublicCalendarToken}
                  onTokenChanged={setHasPublicCalendarToken}
                />
              )}

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="secondary"
                  type="button"
                  onClick={() => navigate(`/buildings/${buildingId}/rooms`)}
                >
                  {t('rooms.cancel')}
                </Button>
                <Button variant="primary" type="submit" loading={submitting}>
                  {t('rooms.save')}
                </Button>
              </div>
            </form>
          </LayerCard.Primary>
        </LayerCard>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  PublicCalendarSection                                              */
/* ------------------------------------------------------------------ */

interface PublicCalendarSectionProps {
  buildingId: string
  roomId: string
  hasToken: boolean
  onTokenChanged: (newHasToken: boolean) => void
}

function PublicCalendarSection(props: PublicCalendarSectionProps) {
  const { getAccessTokenSilently } = useAuth0()
  const { t } = useTranslation()
  const toasts = useKumoToastManager()

  const [subscribeUrl, setSubscribeUrl] = useState<string | null>(null)
  const [isWorking, setIsWorking] = useState(false)
  const [confirmRegenerate, setConfirmRegenerate] = useState(false)
  const [confirmRevoke, setConfirmRevoke] = useState(false)

  // When the room already has a token, fetch its current subscribe URL on
  // mount so admins can see/copy it WITHOUT regenerating (which would
  // immediately break any active iCal subscribers). The admin-only GET
  // endpoint mirrors POST/DELETE: same auth gate, no escalation. We
  // deliberately swallow errors here — failing to fetch the URL should not
  // block the rest of the room form; the Regenerate button stays available
  // as a fallback.
  useEffect(() => {
    if (!props.hasToken) {
      setSubscribeUrl(null)
      return
    }
    let cancelled = false
    async function loadUrl() {
      try {
        const auth = await getAccessTokenSilently()
        const result = await getPublicCalendarSubscribeInfo(props.buildingId, props.roomId, auth)
        if (!cancelled && result.subscribeUrl) {
          setSubscribeUrl(result.subscribeUrl)
        }
      } catch {
        // Non-fatal — leave the input empty; admins can still Regenerate.
      }
    }
    void loadUrl()
    return () => {
      cancelled = true
    }
  }, [props.hasToken, props.buildingId, props.roomId, getAccessTokenSilently])

  // Only first-time Generate fires without a prompt. REGENERATE and REVOKE
  // show a ConfirmDialog because both actions immediately break any active
  // iCal subscribers (Outlook / Google / Apple Calendar polling the old
  // URL will start receiving 404 and silently stop refreshing).
  async function doGenerate() {
    setIsWorking(true)
    try {
      const auth = await getAccessTokenSilently()
      const result = await generatePublicCalendarToken(props.buildingId, props.roomId, auth)
      setSubscribeUrl(result.subscribeUrl)
      props.onTokenChanged(result.token !== null)
      setConfirmRegenerate(false)
    } catch (err) {
      toasts.add({
        variant: 'error',
        title: t('common.errorTitle'),
        description: err instanceof ApiError ? err.message : t('common.error'),
      })
    } finally {
      setIsWorking(false)
    }
  }

  async function doRevoke() {
    setIsWorking(true)
    try {
      const auth = await getAccessTokenSilently()
      await revokePublicCalendarToken(props.buildingId, props.roomId, auth)
      setSubscribeUrl(null)
      props.onTokenChanged(false)
      setConfirmRevoke(false)
    } catch (err) {
      toasts.add({
        variant: 'error',
        title: t('common.errorTitle'),
        description: err instanceof ApiError ? err.message : t('common.error'),
      })
    } finally {
      setIsWorking(false)
    }
  }

  const hasToken = props.hasToken

  return (
    <div className="flex flex-col gap-2 rounded-md border border-kumo-line p-4">
      <h3 className="text-sm font-semibold">{t('roomForm.publicCalendar.title')}</h3>
      <p className="text-xs text-kumo-subtle">{t('roomForm.publicCalendar.help')}</p>

      {hasToken ? (
        <>
          <p className="text-sm">{t('roomForm.publicCalendar.statusEnabled')}</p>
          {subscribeUrl && (
            <div className="flex items-center gap-2">
              <InlineCopyText value={subscribeUrl} truncate>
                {subscribeUrl}
              </InlineCopyText>
            </div>
          )}
          <div className="flex gap-2">
            <Button
              variant="secondary"
              type="button"
              onClick={() => setConfirmRegenerate(true)}
              disabled={isWorking}
            >
              {t('roomForm.publicCalendar.regenerate')}
            </Button>
            <Button
              variant="primary"
              type="button"
              onClick={() => setConfirmRevoke(true)}
              disabled={isWorking}
            >
              {t('roomForm.publicCalendar.revoke')}
            </Button>
          </div>
          <p className="text-xs text-kumo-warning">
            {t('roomForm.publicCalendar.regenerateWarning')}
          </p>
        </>
      ) : (
        <div className="flex items-center gap-2">
          <p className="text-sm">{t('roomForm.publicCalendar.statusDisabled')}</p>
          <Button
            variant="primary"
            type="button"
            onClick={() => void doGenerate()}
            disabled={isWorking}
          >
            {t('roomForm.publicCalendar.generate')}
          </Button>
        </div>
      )}

      <ConfirmDialog
        open={confirmRegenerate}
        onOpenChange={setConfirmRegenerate}
        title={t('roomForm.publicCalendar.regenerateConfirmTitle')}
        description={t('roomForm.publicCalendar.regenerateConfirm')}
        confirmLabel={t('roomForm.publicCalendar.regenerate')}
        loading={isWorking}
        onConfirm={() => void doGenerate()}
      />
      <ConfirmDialog
        open={confirmRevoke}
        onOpenChange={setConfirmRevoke}
        title={t('roomForm.publicCalendar.revokeConfirmTitle')}
        description={t('roomForm.publicCalendar.revokeConfirm')}
        confirmLabel={t('roomForm.publicCalendar.revoke')}
        loading={isWorking}
        onConfirm={() => void doRevoke()}
      />
    </div>
  )
}
